import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { GetAuthors } from '@root/modules/author/types/author';
import getAuthors from '@root/modules/author/graphql/getAuthors.graphql';
import getAuthorsForPage from '@root/modules/author/graphql/getAuthorsForPage.graphql';
import { dataLoader } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = GetAuthors['variables'];
type Response = GetAuthors['data'];
type FetchAuthorsReturn = Response['authors']['data'];

export default class AuthorsService extends Service implements ServiceInterface {
  private retriedFailedRequest = false;

  private async fetchAuthors(variables: ServiceVariables, isPageQuery: boolean): Promise<FetchAuthorsReturn | undefined> {
    try {
      const options = Object.assign({ query: isPageQuery ? getAuthorsForPage : getAuthors }, { variables });
      const dataLoaderOptions = {
        remote: {
          keyPrefix: 'authors',
        },
      };

      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const [response] = await apiProvider.query<Response>(options);

        return response.data;
      };

      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      return response.authors.data;
    } catch (e) {
      // Retry failed request one time
      if (!this.retriedFailedRequest) {
        this.retriedFailedRequest = true;

        return await this.fetchAuthors(variables, isPageQuery);
      }

      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: ServiceVariables, isPageQuery: boolean = false) {
    const response = await this.fetchAuthors(variables, isPageQuery);

    return response;
  }
}
